<script setup>
import { computed, useTemplateRef } from 'vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  room: { type: Object, required: true },
  agentsById: { type: Object, required: true },
  currentUserId: { type: [Number, String], default: null },
  title: { type: String, required: true },
  infoSectionTitle: { type: String, required: true },
  kindGroupLabel: { type: String, required: true },
  kindDirectLabel: { type: String, required: true },
  participantsLabel: { type: String, required: true },
  createdByLabel: { type: String, required: true },
  noMembersLabel: { type: String, required: true },
  youLabel: { type: String, required: true },
  kindLabel: { type: String, required: true },
  sharedFilesLabel: { type: String, required: true },
  noSharedFilesLabel: { type: String, required: true },
  attachments: { type: Array, required: true },
  groupAvatarUrl: { type: String, default: '' },
  canEditGroupAvatar: { type: Boolean, default: false },
  isSavingGroupAvatar: { type: Boolean, default: false },
});

const emit = defineEmits(['updateGroupAvatar', 'clearGroupAvatar']);
const groupAvatarInputRef = useTemplateRef('groupAvatarInputRef');

const members = computed(() =>
  Array.isArray(props.room?.members) ? props.room.members : []
);

const membersOnline = computed(() =>
  members.value.filter(member => {
    const agent = props.agentsById?.[String(member?.id)];
    return agent?.availability_status === 'online';
  })
);

const membersOffline = computed(() =>
  members.value.filter(member => {
    const agent = props.agentsById?.[String(member?.id)];
    return agent?.availability_status !== 'online';
  })
);

const createdByMember = computed(() =>
  members.value.find(
    member => Number(member?.id) === Number(props.room?.created_by_id)
  )
);

function displayMemberName(member) {
  return member?.available_name || member?.name || '';
}

function attachmentName(attachment) {
  return attachment?.filename || attachment?.file_type || 'Arquivo';
}

function isImageAttachment(attachment) {
  return String(attachment?.content_type || '').startsWith('image/');
}

function memberOnlineStatus(member) {
  const agent = props.agentsById?.[String(member?.id)];
  return agent?.availability_status === 'online' ? 'online' : 'offline';
}

const FILE_PREVIEW_PALETTE = [
  'bg-n-brand text-white',
  'bg-n-teal-9 text-white',
  'bg-n-iris-9 text-white',
  'bg-n-slate-9 text-white',
  'bg-n-ruby-9 text-white',
  'bg-n-amber-9 text-white',
];

function fileExtension(attachment) {
  const name = String(attachment?.filename || '').trim().toLowerCase();
  if (!name.includes('.')) return 'FILE';
  const ext = name.split('.').pop();
  if (!ext) return 'FILE';
  return ext.slice(0, 4).toUpperCase();
}

function filePreviewColorClass(attachment) {
  const ext = fileExtension(attachment);
  let hash = 0;
  for (let i = 0; i < ext.length; i += 1) {
    hash = (hash << 5) - hash + ext.charCodeAt(i);
    hash |= 0;
  }
  return FILE_PREVIEW_PALETTE[Math.abs(hash) % FILE_PREVIEW_PALETTE.length];
}

const groupAvatarName = computed(
  () => props.room?.display_name || props.room?.name || 'Grupo'
);

function openGroupAvatarPicker() {
  if (!props.canEditGroupAvatar || props.isSavingGroupAvatar) return;
  groupAvatarInputRef.value?.click();
}

function onGroupAvatarFileSelected(event) {
  const [file] = Array.from(event.target?.files || []);
  event.target.value = '';
  if (!file) return;
  emit('updateGroupAvatar', file);
}
</script>

<template>
  <div class="overflow-y-auto flex-1 flex flex-col gap-4 px-4 pb-4 min-h-0">
    <div
      v-if="room.kind === 'multi_user'"
      class="rounded-xl border border-n-weak bg-n-background"
    >
      <input
        ref="groupAvatarInputRef"
        type="file"
        accept="image/*"
        class="hidden"
        @change="onGroupAvatarFileSelected"
      />
      <div class="flex gap-3 items-center px-4 py-3">
        <Avatar
          :name="groupAvatarName"
          :src="groupAvatarUrl || ''"
          :size="44"
          rounded-full
        />
        <div class="flex flex-col flex-1 min-w-0">
          <span class="text-sm font-semibold truncate text-n-slate-12">
            Foto do grupo
          </span>
          <span class="text-xs text-n-slate-10">
            Defina a foto de perfil desta conversa
          </span>
        </div>
        <div
          v-if="room.kind === 'multi_user' && canEditGroupAvatar"
          class="flex items-center gap-2"
        >
          <Button
            variant="faded"
            color="slate"
            size="sm"
            icon="i-lucide-camera"
            label="Definir"
            :is-loading="isSavingGroupAvatar"
            :disabled="isSavingGroupAvatar"
            @click="openGroupAvatarPicker"
          />
          <Button
            v-if="groupAvatarUrl"
            variant="faded"
            color="ruby"
            size="sm"
            icon="i-lucide-trash-2"
            label="Remover"
            :is-loading="isSavingGroupAvatar"
            :disabled="isSavingGroupAvatar"
            @click="emit('clearGroupAvatar')"
          />
        </div>
      </div>
    </div>
    <div
      v-if="room.kind === 'multi_user'"
      class="rounded-xl border border-n-weak bg-n-background"
    >
      <div class="px-4 py-3 border-b border-n-weak/80">
        <h3 class="m-0 text-sm font-semibold text-n-slate-12">{{ infoSectionTitle }}</h3>
      </div>
      <div class="px-4">
        <div class="flex justify-between items-center gap-4 py-3 border-b border-n-weak/60">
          <span class="inline-flex items-center gap-1.5 text-xs font-medium text-n-slate-10">
            <span class="i-lucide-hash size-3.5" aria-hidden="true" />
            {{ kindLabel }}
          </span>
          <span class="text-sm font-medium text-right text-n-slate-12">
            {{ room.kind === 'multi_user' ? kindGroupLabel : kindDirectLabel }}
          </span>
        </div>
        <div class="flex justify-between items-center gap-4 py-3 border-b border-n-weak/60">
          <span class="inline-flex items-center gap-1.5 text-xs font-medium text-n-slate-10">
            <span class="i-lucide-user-round-cog size-3.5" aria-hidden="true" />
            {{ createdByLabel }}
          </span>
          <span class="text-sm font-medium text-right text-n-slate-12">
            {{ displayMemberName(createdByMember) || '-' }}
          </span>
        </div>
      </div>
    </div>

    <div
      v-if="room.kind === 'multi_user'"
      class="rounded-xl border border-n-weak bg-n-background"
    >
      <div class="flex justify-between items-center px-4 py-3 border-b border-n-weak/80">
        <h3 class="m-0 text-sm font-semibold text-n-slate-12">{{ participantsLabel }}</h3>
        <span
          class="inline-flex justify-center items-center px-2 min-w-[1.5rem] h-5 text-xxs font-medium rounded-full bg-n-alpha-2 text-n-slate-11 tabular-nums"
        >
          {{ members.length }}
        </span>
      </div>
      <div v-if="members.length === 0" class="px-4 py-4 text-sm text-center text-n-slate-11">
        {{ noMembersLabel }}
      </div>
      <template v-else>
        <div class="px-4 py-2.5 border-b border-n-weak/60 bg-n-alpha-1">
          <div class="flex justify-between items-center">
            <span class="text-xs font-medium text-n-slate-11">Online</span>
            <span class="text-xxs font-medium text-n-teal-11 tabular-nums">
              {{ membersOnline.length }}
            </span>
          </div>
        </div>
        <ul class="p-0 m-0 list-none divide-y divide-n-weak/60">
          <li
            v-if="membersOnline.length === 0"
            class="px-4 py-3 text-xs text-n-slate-10"
          >
            Nenhum participante online
          </li>
          <li
            v-for="member in membersOnline"
            :key="`online-${member.id}`"
            class="flex gap-2 items-center px-4 py-2.5"
          >
            <div class="relative shrink-0">
              <Avatar
                :name="displayMemberName(member)"
                :src="member.thumbnail || member.avatar_url || ''"
                :size="28"
                rounded-full
              />
              <span
                class="absolute -right-0.5 -bottom-0.5 size-2.5 rounded-full ring-2 ring-n-background"
                :class="memberOnlineStatus(member) === 'online' ? 'bg-n-teal-9' : ''"
                :style="
                  memberOnlineStatus(member) === 'offline'
                    ? { backgroundColor: '#F59E0B' }
                    : undefined
                "
                aria-hidden="true"
              />
            </div>
            <div class="flex flex-col flex-1 min-w-0">
              <span class="text-sm font-medium truncate text-n-slate-12">
                {{ displayMemberName(member) }}
              </span>
              <span v-if="member.email" class="text-xs truncate text-n-slate-10">
                {{ member.email }}
              </span>
            </div>
            <span
              v-if="Number(member.id) === Number(currentUserId)"
              class="text-xxs font-medium text-n-brand"
            >
              {{ youLabel }}
            </span>
          </li>
        </ul>
        <div class="px-4 py-2.5 border-y border-n-weak/60 bg-n-alpha-1">
          <div class="flex justify-between items-center">
            <span class="text-xs font-medium text-n-slate-11">Offline</span>
            <span class="text-xxs font-medium text-n-slate-10 tabular-nums">
              {{ membersOffline.length }}
            </span>
          </div>
        </div>
        <ul class="p-0 m-0 list-none divide-y divide-n-weak/60">
          <li
            v-if="membersOffline.length === 0"
            class="px-4 py-3 text-xs text-n-slate-10"
          >
            Nenhum participante offline
          </li>
          <li
            v-for="member in membersOffline"
            :key="`offline-${member.id}`"
            class="flex gap-2 items-center px-4 py-2.5"
          >
            <div class="relative shrink-0">
              <Avatar
                :name="displayMemberName(member)"
                :src="member.thumbnail || member.avatar_url || ''"
                :size="28"
                rounded-full
              />
              <span
                class="absolute -right-0.5 -bottom-0.5 size-2.5 rounded-full ring-2 ring-n-background"
                :class="memberOnlineStatus(member) === 'online' ? 'bg-n-teal-9' : ''"
                :style="
                  memberOnlineStatus(member) === 'offline'
                    ? { backgroundColor: '#F59E0B' }
                    : undefined
                "
                aria-hidden="true"
              />
            </div>
            <div class="flex flex-col flex-1 min-w-0">
              <span class="text-sm font-medium truncate text-n-slate-12">
                {{ displayMemberName(member) }}
              </span>
              <span v-if="member.email" class="text-xs truncate text-n-slate-10">
                {{ member.email }}
              </span>
            </div>
            <span
              v-if="Number(member.id) === Number(currentUserId)"
              class="text-xxs font-medium text-n-brand"
            >
              {{ youLabel }}
            </span>
          </li>
        </ul>
      </template>
    </div>

    <div class="rounded-xl border border-n-weak bg-n-background">
      <div class="px-4 py-3 border-b border-n-weak/80">
        <h3 class="m-0 text-sm font-semibold text-n-slate-12">{{ sharedFilesLabel }}</h3>
      </div>
      <ul class="p-0 m-0 list-none divide-y divide-n-weak">
        <li
          v-if="attachments.length === 0"
          class="px-4 py-4 text-sm text-center text-n-slate-11"
        >
          {{ noSharedFilesLabel }}
        </li>
        <li
          v-for="attachment in attachments"
          :key="`shared-file-${attachment.id || attachment.url || attachment.filename}`"
          class="px-4 py-2.5"
        >
          <a
            :href="attachment.url"
            target="_blank"
            rel="noopener noreferrer"
            class="flex gap-2 items-center no-underline text-n-slate-12 hover:text-n-slate-12"
          >
            <img
              v-if="isImageAttachment(attachment)"
              :src="attachment.url"
              :alt="attachmentName(attachment)"
              class="rounded-md object-cover size-8 shrink-0 border border-n-weak"
            />
            <span
              v-else
              class="inline-flex justify-center items-center text-xxs font-semibold rounded-md size-8 shrink-0 tabular-nums"
              :class="filePreviewColorClass(attachment)"
            >
              {{ fileExtension(attachment) }}
            </span>
            <span class="flex flex-col min-w-0">
              <span class="text-sm font-medium truncate">
                {{ attachmentName(attachment) }}
              </span>
              <span v-if="attachment.file_type" class="text-xxs text-n-slate-10">
                {{ attachment.file_type }}
              </span>
            </span>
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>
